import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../utils/services/authService';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    /**
     * Initialize auth state from localStorage on mount
     */
    useEffect(() => {
        checkAuthStatus();
    }, []);

    /**
     * Check if user is authenticated and token is valid
     */
    const checkAuthStatus = () => {
        const token = authService.getToken();
        
        if (token && authService.isTokenValid()) {
            const payload = authService.decodeToken();
            if (payload) {
                setUser({
                    userId: payload.userId,
                    email: payload.email,
                    roles: payload.roles || [],
                    subscriptions: payload.subscriptions || [],
                    discordLinked: payload.discord_linked || false
                });
                setIsAuthenticated(true);
            } else {
                // Invalid token, clear it
                authService.logout();
            }
        }
        
        setIsLoading(false);
    };

    /**
     * Login with email and password
     * @param {string} email - User email
     * @param {string} password - User password
     * @returns {Promise<object>} Result object with success status
     */
    const login = async (email, password) => {
        try {
            const response = await authService.login(email, password);
            
            if (response.success && response.data.success) {
                const { token } = response.data;
                
                // Store token
                authService.setToken(token);
                
                // Decode token and set user data
                const payload = authService.decodeToken();
                
                if (payload) {
                    const userData = {
                        userId: payload.userId,
                        email: payload.email,
                        roles: payload.roles || [],
                        subscriptions: payload.subscriptions || [],
                        discordLinked: payload.discord_linked || false
                    };
                    
                    setUser(userData);
                    setIsAuthenticated(true);
                    
                    return { 
                        success: true, 
                        message: response.data.message || 'Login successful'
                    };
                }
            }
            
            return { 
                success: false, 
                error: response.message || 'Login failed. Please check your credentials.'
            };
        } catch (error) {
            console.error('Login error:', error);
            return { 
                success: false, 
                error: 'An unexpected error occurred. Please try again.'
            };
        }
    };

    /**
     * Logout user and clear stored data
     */
    const logout = () => {
        authService.logout();
        setUser(null);
        setIsAuthenticated(false);
    };

    /**
     * Check if user has a specific subscription
     * @param {string} subscriptionKey - Subscription key to check
     * @returns {boolean} True if user has the subscription
     */
    const hasSubscription = (subscriptionKey) => {
        if (!user || !user.subscriptions) return false;
        return user.subscriptions.includes(subscriptionKey);
    };

    /**
     * Check if user has a specific role
     * @param {string} role - Role to check
     * @returns {boolean} True if user has the role
     */
    const hasRole = (role) => {
        if (!user || !user.roles) return false;
        return user.roles.includes(role);
    };

    /**
     * Check token expiration periodically (every minute)
     */
    useEffect(() => {
        if (isAuthenticated) {
            const interval = setInterval(() => {
                if (!authService.isTokenValid()) {
                    // Token expired, logout user and set session expired flag
                    sessionStorage.setItem('sessionExpired', 'true');
                    logout();
                }
            }, 60000); // Check every minute

            return () => clearInterval(interval);
        }
    }, [isAuthenticated]);

    const value = {
        user,
        isAuthenticated,
        isLoading,
        login,
        logout,
        hasSubscription,
        hasRole
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
